import Link from 'next/link';
import { Clock, Goal, MessageSquare, Repeat2 } from 'lucide-react';
import { useBasePath } from '../../../lib/use-base-path';
import { useTranslation } from '../../../lib/i18n';
import type { GoalOrigin, RoutineOrigin } from '../../../lib/task-origin';
import { TextButton } from '../ui';
import { useInCategoryList } from '../category-view/CategoryView';
import styles from './TaskOriginLine.module.css';

// "From goal — Autumn book club": one quiet line saying where a task came from, with the goal's name as
// the link to it. Rendered on the task ROW, on a collapsed goal fold's header, and in the task DETAIL, so
// the answer to "why is this row here" is in the same place and the same weight wherever you meet it.
//
// **It sits ABOVE the card, not inside it.** The whole row is a button element (the card opens the task's
// detail), and an anchor nested inside a button is invalid HTML that browsers resolve unpredictably — the
// same constraint that puts the row's overflow menu outside the Card. So the line is a sibling in the row
// wrapper, which also reads correctly: it is a header FOR the row, not a field of it.
//
// The glyph is `target`, the goals mark, chosen to sit beside the routines mark (`repeat-2`) the fold
// header already wears — two origins, two marks, one line shape.
/**
 * The same line for a task whose origin is the CHAT it was born in — every task has one, where a goal is
 * only some tasks' origin (see `taskChatOrigin`). Rendered only when that conversation is one the reader
 * can actually be dropped into, so the name always has a destination.
 *
 * It is a BUTTON, not a Link: a conversation is opened in place (the side chat swaps threads) rather than
 * navigated to, so there is no href to give an anchor. The goal variant above stays a real Link precisely
 * because a goal IS a route.
 */
/** "Scheduled for Thu, 9 Oct" — the dated task's own line, under the origin line. */
export function TaskScheduledLine({ when }: { when: string }) {
  const { t } = useTranslation();
  return (
    <div className={styles.line}>
      <Clock size={14} aria-hidden className={styles.glyph} />
      <span>{t('tasks.detail.scheduledFor', { when })}</span>
    </div>
  );
}

export function TaskChatOriginLine({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();

  return (
    <div className={`${styles.line} ${styles.detail}`}>
      <MessageSquare size={16} aria-hidden className={styles.glyph} />
      {/* The DS TextButton: no box, so the line is exactly as tall as the goal variant's text. There is no
          route to a thread, so it is a button, not a link; underlined like the goal's name, the other
          origin the detail can name. */}
      <TextButton
        size="tiny"
        underline
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        {t('tasks.origin.chat')}
      </TextButton>
    </div>
  );
}

/** `detail` is the task drill-in's look (Figma 20846-93457): the 16px goal glyph, the whole line in the
 *  secondary SemiBold, the name underlined. The row and fold headers keep the quieter line. */
export function TaskOriginLine({ origin, onOpen, detail = false }: { origin: GoalOrigin; onOpen?: () => void; detail?: boolean }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  // In a category list the line is the group's header row ("◎ From goal Sell Bananas in Europe").
  const inList = useInCategoryList();

  return (
    <div className={[styles.line, detail && styles.detail, inList && styles.lineInList].filter(Boolean).join(' ')}>
      <Goal size={detail || inList ? 16 : 14} aria-hidden className={styles.glyph} />
      <span className={styles.label}>{t('tasks.origin.goal')}</span>
      {origin.href ? (
        // `stopPropagation`, because the card behind this line opens the TASK: without it a click on the
        // goal's name would navigate and open the task detail at the same time.
        <Link
          href={`${basePath}${origin.href}`}
          className={styles.name}
          dir="auto"
          onClick={(event) => {
            event.stopPropagation();
            onOpen?.();
          }}
        >
          {origin.title}
        </Link>
      ) : (
        // No resolvable workspace slug — see `GoalOrigin.href`. The goal is still named; it just doesn't
        // pretend to be openable.
        <span className={styles.name} dir="auto">
          {origin.title}
        </span>
      )}
    </div>
  );
}

/**
 * The task detail's line for a task a ROUTINE created: the routines mark (`repeat-2`, the glyph the list's
 * routine fold wears), "From routine", and the routine's name, which opens the routine's own drill-in on
 * top of the task. A button, not a Link, because that drill-in is a stacked modal rather than a route.
 * Shown ABOVE the goal or chat line, not in its place: the routine is what makes another task when this
 * one is cancelled, so it is said even when a goal or chat is named too.
 */
export function TaskRoutineOriginLine({ origin, onOpen }: { origin: RoutineOrigin; onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={`${styles.line} ${styles.detail}`} data-testid="taskOriginRoutine">
      <Repeat2 size={16} aria-hidden className={styles.glyph} />
      <span className={styles.label}>{t('tasks.origin.routine')}</span>
      <TextButton
        size="tiny"
        underline
        className={styles.nameButton}
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        <span dir="auto">{origin.title}</span>
      </TextButton>
    </div>
  );
}
